import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import authService from "../services/discordAuth";
import "./NotificationBell.css";

function NotificationBell() {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 70,
    left: 0,
  });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    // Fechar dropdown ao clicar fora
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Buscar notificações do backend
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!authService.isAuthenticated()) {
        return;
      }

      try {
        const token = authService.getAccessToken();
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

        const response = await fetch(`${BACKEND_URL}/api/notifications?limit=10`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications);
          setUnreadCount(data.pagination.unread);
        } else if (response.status === 401) {
          // Se deu 401, a sessão expirou. Parar de buscar para evitar spam de erros.
          console.warn("🔔 Sessão expirada ao buscar notificações.");
          authService.clearStorage();
        }
      } catch (error) {
        console.error("Erro ao buscar notificações:", error);
      }
    };

    // Buscar notificações inicialmente
    fetchNotifications();

    // Atualizar notificações a cada 30 segundos
    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleToggleDropdown = () => {
    if (!showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 380;

      // Calcula a posição para alinhar o dropdown à direita do botão
      let leftPosition = rect.right - dropdownWidth;

      // Ajusta se sair da tela à esquerda
      if (leftPosition < 10) leftPosition = 10;

      // Ajusta se sair da tela à direita
      if (leftPosition + dropdownWidth > window.innerWidth - 10) {
        leftPosition = window.innerWidth - dropdownWidth - 10;
      }

      setDropdownPosition({
        top: rect.bottom + 8,
        left: leftPosition,
      });
    }

    setShowDropdown(!showDropdown);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = authService.getAccessToken();
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      const response = await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Atualizar estado local
        setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Erro ao marcar notificação como lida:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = authService.getAccessToken();
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      const response = await fetch(`${BACKEND_URL}/api/notifications/read-all`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Atualizar estado local
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Erro ao marcar todas as notificações como lidas:", error);
    }
  };

  // Não mostrar se não estiver logado
  if (!authService.isAuthenticated()) {
    return null;
  }

  return (
    <div className="notification-bell-container d-none d-md-none d-lg-block">
      <button ref={buttonRef} className="notification-bell-button" onClick={handleToggleDropdown} aria-label={t("notifications_bell.bellAria")}>
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
      </button>

      {showDropdown && (
        <div
          className="notification-dropdown"
          ref={dropdownRef}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <div className="notification-header">
            <h3>{t("notifications_bell.title")}</h3>
            {unreadCount > 0 && (
              <button className="mark-all-read-btn" onClick={handleMarkAllAsRead}>
                {t("notifications_bell.markAllAsRead")}
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div key={notification.id} className={`notification-item ${notification.read ? "read" : "unread"}`} onClick={() => handleMarkAsRead(notification.id)}>
                  <div className="notification-icon">{notification.icon}</div>
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">{notification.time}</div>
                  </div>
                  {!notification.read && <div className="unread-dot"></div>}
                </div>
              ))
            ) : (
              <div className="no-notifications">
                <span className="no-notifications-icon">📭</span>
                <p>{t("notifications_bell.noNotifications")}</p>
              </div>
            )}
          </div>

          <div className="notification-footer">
            <Link to="/notifications" onClick={() => setShowDropdown(false)}>
              {t("notifications_bell.viewAll")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
